import React from 'react';

interface PromoCodeSectionProps {
  promoCode: string;
  setPromoCode: (code: string) => void;
  promoApplied: boolean;
  promoError: string;
  setPromoError: (error: string) => void;
  promoValidating?: boolean;
  onApply: () => void;
  onRemove: () => void;
  onRequestCode: () => void;
}

const PromoCodeSection: React.FC<PromoCodeSectionProps> = ({
  promoCode,
  setPromoCode,
  promoApplied,
  promoError,
  setPromoError,
  promoValidating = false,
  onApply,
  onRemove,
  onRequestCode,
}) => {
  return (
    <div className="mt-8 max-w-md mx-auto">
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border border-yellow-200 dark:border-yellow-700/50 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xl">🎁</span>
          <span className="font-semibold text-gray-900 dark:text-white">Have a promo code?</span>
        </div>
        {promoApplied ? (
          <div className="flex items-center justify-between bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-lg px-4 py-3">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium text-green-800 dark:text-green-300">
                <span className="font-mono">{promoCode}</span> - 40% OFF applied!
              </span>
            </div>
            <button
              onClick={onRemove}
              className="text-green-700 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 text-sm font-medium"
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => {
                setPromoCode(e.target.value.toUpperCase());
                setPromoError('');
              }}
              placeholder="Enter promo code"
              disabled={promoValidating}
              className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 ${
                promoError ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-slate-600'
              } ${promoValidating ? 'bg-gray-100 dark:bg-slate-700 cursor-not-allowed' : ''}`}
            />
            <button
              onClick={onApply}
              disabled={promoValidating}
              className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[100px]"
            >
              {promoValidating ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Validating</span>
                </span>
              ) : (
                'Apply'
              )}
            </button>
          </div>
        )}
        {promoError && (
          <p className="mt-2 text-sm text-red-600">{promoError}</p>
        )}
        {!promoApplied && !promoError && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Don't have a code?{' '}
            <button
              onClick={onRequestCode}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
            >
              Request one here
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

export default PromoCodeSection;
